import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';

import { DateTime, ExprCall, ExprForm } from '@mode-switch/express';
import { Attr, Names } from '@mode-switch/model';
import {
  EncDualAxisXField,
  EncDualAxisYField,
  EncField,
  EncSort,
  Encoding,
  EncodingResult,
  ExprFrame,
  SortType,
  VariableType,
} from '@mode-vegas/vegas';
import { IconComponent, TooltipDelay } from '@mode/capra';
import { ChartFormatHelpers, EncodingHelpers, SharedAxisHelpers, SwitchSchemaModel } from '@mode/charts/util-vegas';
import { BinSizeTypes, ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { CompiledField, FormulaOperations, ScalarExpr, Switch } from '@mode/shared/util-switch';
import { ContextMenuComponent, ContextMenuModule, ContextMenuService } from '@perfectmemory/ngx-contextmenu';
import { Observable } from 'rxjs';
import { BinningModalService } from '../binning-modal/binning-modal.service';
import * as FieldHelpers from '../helpers/field-helpers';
import { SortModalDialogService } from '../sort-modal';
import { TableCalcDialogService } from '../table-calc-dialog';
import { EncodingPillBaseComponent } from './encoding-pill-base.component';
import { UpdateFieldService } from './update-field.service';

@Component({
  standalone: true,
  imports: [CommonModule, ContextMenuModule, EncodingPillBaseComponent, IconComponent],
  selector: 'mode-encoding-field-pill',
  templateUrl: './encoding-field-pill.component.html',
  styleUrls: ['./encoding-field-pill.component.scss'],
})
export class EncodingFieldPillComponent implements OnChanges {
  @Input({ required: true }) field!: EncField;
  @Input({ required: true }) encoding!: Encoding;
  @Input() fieldFormatMap: ChartTypes.FieldFormatMap | undefined;
  @Input() format: ChartTypes.ChartFormat | undefined;
  @Input({ required: true }) model!: FlamingoTypes.ModeModel;
  @Input({ required: true }) viewName!: string;
  @Input({ required: true }) vizToken!: string;
  @Input({ required: true }) chartType!: ChartTypes.ChartType;
  @Input() binSizeToFieldMap: BinSizeTypes.BinSizeToFieldMap | undefined;

  @Input() allowScalars = false;
  @Input() allowAggregates = false;
  @Input() allowQuickCalcs = false;
  @Input() allowContinuous = false;
  @Input() allowDiscrete = false;
  @Input() allowBinned = true;

  @Input() canLegendBeSplit = false;
  @Input() hasSplitLegend = false;
  @Input() showColorSettings = false;
  @Input() hideAllMenuOptions = false;

  @Output() update = new EventEmitter<{ $result: EncodingResult; $formats?: ChartTypes.FieldFormatMap }>();
  @Output() remove = new EventEmitter<{ $field: EncField }>();
  @Output() removeSort = new EventEmitter();
  @Output() toggleDualAxis = new EventEmitter<{
    $field: CompiledField;
    $sibling: EncField | undefined;
    $channel: 'row' | 'column';
  }>();
  @Output() isValidField: EventEmitter<{ isValid: boolean; field: EncField }> = new EventEmitter<{
    isValid: boolean;
    field: EncField;
  }>();
  @Output() splitLegend = new EventEmitter<boolean>();

  @Output() getBinSize = new EventEmitter<{ columnName: string; token: string }>();

  // set by onChanges
  view: FlamingoTypes.ModeView | undefined;
  schemaModel: SwitchSchemaModel | undefined;
  formula: CompiledField | undefined;

  isValid = false;
  isNames = false;
  isValues = false;
  isSystem = false;
  isContinuous = false;
  isDiscrete = false;
  isDatePart = false;
  isDateTrunc = false;
  isDateExpr = false;
  isQuickChartType = false;
  dateTruncOpts: Array<FieldHelpers.DateUnitOpt> = [];
  dateTruncMoreOpts: Array<FieldHelpers.DateUnitOpt> = [];
  datePartOpts: Array<FieldHelpers.DateUnitOpt> = [];
  datePartMoreOpts: Array<FieldHelpers.DateUnitOpt> = [];
  aggregateOpts: Array<FieldHelpers.AggFnOpt> = [];
  isExactDateAndTime = false;

  formType: ExprForm | undefined;
  weekStart: DateTime.WeekStart | undefined;
  yearStart: DateTime.YearStart | undefined;
  defaultWeekStart: DateTime.WeekStart | undefined;
  defaultYearStart: DateTime.YearStart | undefined;

  aggFunc: string | undefined;
  sortDirectionIcon: string | undefined;

  isAggregateStage = false;
  isAnalyticCalc = false;
  hasAnalyticFunc = false;
  isVE = false;
  isVegasPivotTable = false;
  isMuzeRenderer = false;

  displayName = '';
  errorMessage = '';

  hasSortOpt = false;
  hasClearSortOpt = false;
  hasDateFuncMenuOpts = false;
  hasDualAxisOpts = false;

  hasExactDateTimeMenuOpt = false;
  hasNoAggregateMenuOpt = false;
  hasVarTypeMenuOpts = false;

  public weekStartOptions = FieldHelpers.WeekStartOptions;
  public yearStartOptions = FieldHelpers.YearStartOptions;

  public variableDiscrete = VariableType.DISCRETE;
  public variableContinuous = VariableType.CONTINUOUS;

  public expressionPctDiff = ExprForm.PctDifference;
  public expressionDiff = ExprForm.Difference;
  public expressionPctOfTotal = ExprForm.PctOfTotal;

  public isX2 = false;
  public isY2 = false;
  public sibling: EncField | undefined;
  public isX2Relative = false;
  public isY2Relative = false;
  public isDualAxis = false;
  public isDualAxisRelative = false;

  public tooltipQuickCalcDescription: string | null = '';
  public encSort: EncSort | undefined;

  public canBeSharedAxis = false;
  public isSharedAxis = false;
  public sharedAxisId = '';

  public canBeBinned = false;
  public isBinned = false;
  public disableBinningForValues = false;

  public caretIcon = 'icon-invalid';

  readonly tooltipInteractive = false;
  readonly tooltipAppendTo: Element | undefined;
  readonly tooltipDelay: TooltipDelay = 500;

  public isCustomCalenderSettingsEnabled$: Observable<boolean>;
  public preBinAggFunc?: ExprCall.Name | null;
  pendingBinSizeMap = new Map<
    string,
    {
      field: EncField;
      formula: CompiledField;
    }
  >();

  @ViewChild(ContextMenuComponent) public menu: ContextMenuComponent | undefined;

  constructor(
    private updateFieldService: UpdateFieldService,
    public contextMenuService: ContextMenuService,
    private sortModal: SortModalDialogService,
    private tableCalcDialogService: TableCalcDialogService,
    private featureFlagsFacade: FeatureFlagsFacade,
    private binningModalService: BinningModalService
  ) {
    this.isCustomCalenderSettingsEnabled$ = this.featureFlagsFacade.asObservable(FeatureFlag.CustomCalendar);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.errorMessage = '';

    if (this.field == null) {
      return; // component should be cleaning up
    }

    this.view = Switch.Views.find(this.model, this.viewName);
    if (this.view == null) {
      return;
    }
    const compiled = CompiledField.fromEncField(this.view, this.field);
    this.schemaModel = SwitchSchemaModel.make(this.model, this.viewName);

    this.formula = compiled;
    this.isNames = compiled.isNames;
    this.isValues = compiled.isValues;
    this.isSystem = compiled.isSystem;

    this.isContinuous = compiled.isContinuous;
    this.isDiscrete = compiled.isDiscrete;
    this.displayName = compiled.displayFormula;

    this.isAggregateStage = compiled.isAggregateStage;
    this.isAnalyticCalc = compiled.isAnalyticCalc;
    this.hasAnalyticFunc = compiled.hasAnalyticFunc;
    this.formType = compiled.form;

    this.isDatePart = compiled.form === ExprForm.DatePart;
    this.isDateTrunc = compiled.form === ExprForm.DateTrunc;
    this.isDateExpr = compiled.isAttrDateType || this.isDatePart || this.isDateTrunc;

    this.isExactDateAndTime = compiled.form === ExprForm.Access ? compiled.isAttrDateType : false;
    this.weekStart = compiled.hasDateFunc ? (compiled.weekStart ?? compiled.defaultWeekStart) : undefined;
    this.yearStart = compiled.hasDateFunc ? (compiled.yearStart ?? compiled.defaultYearStart) : undefined;
    this.defaultYearStart = compiled.defaultYearStart;
    this.defaultWeekStart = compiled.defaultWeekStart;

    const dateTruncMenuOpts = FieldHelpers.buildDateTruncMenuOpts(compiled);
    this.dateTruncOpts = this.isDateExpr ? dateTruncMenuOpts.mainMenuOpts : [];
    this.dateTruncMoreOpts = this.isDateExpr ? dateTruncMenuOpts.moreMenuOpts : [];

    const datePartMenuOpts = FieldHelpers.buildDatePartMenuOpts(compiled);
    this.datePartOpts = this.isDateExpr ? datePartMenuOpts.mainMenuOpts : [];
    this.datePartMoreOpts = this.isDateExpr ? datePartMenuOpts.moreMenuOpts : [];

    this.encSort = undefined;
    if (this.isDiscrete && this.schemaModel != null) {
      this.encSort = this.encoding.sorts.find((sort) =>
        sort.type === SortType.NESTED
          ? // (Field Level) Nested sorts are by alias
            sort.field === this.field.id
          : // (View Level) Everything else is by formula
            sort.source === this.field.formula
      );
    }

    this.isValid = this.validateEncField(this.field, this.view, this.schemaModel);
    if (this.isValid) {
      this.validateConfig(compiled);
    }

    const isCountable = compiled.isValid && !compiled.isNonScalarCalc; // calculated fields with aggregates cannot be aggregated
    const isSummable = compiled.isAttrNumberType && !compiled.isAttrDateType && !compiled.isNonScalarCalc; // calculated fields with aggregates cannot be aggregated

    this.hasExactDateTimeMenuOpt = this.isValid && this.isDateExpr && this.allowScalars;

    this.hasDateFuncMenuOpts = !this.isSystem && this.isValid && this.isDateExpr && this.allowScalars;

    this.hasNoAggregateMenuOpt = this.getHasNoAggregateMenuOpt(compiled);

    const allowSummableAggregates = !this.isSystem && this.isValid && isSummable && this.allowAggregates;
    const allCountableAggregates = !this.isSystem && this.isValid && !isSummable && isCountable && this.allowAggregates;
    const isSummableOrCountable = allowSummableAggregates || allCountableAggregates;

    this.aggFunc = (compiled.aggFunc || '').toUpperCase();
    this.aggregateOpts = FieldHelpers.AggregateOpts.reduce((visible, aggr) => {
      if ((aggr.summable && allowSummableAggregates) || (aggr.summableOrCountable && isSummableOrCountable)) {
        visible.push({
          name: aggr.label,
          value: aggr.aggregate,
        });
      }
      return visible;
    }, [] as Array<FieldHelpers.AggFnOpt>);

    const canBeDiscrete = !this.encoding.value.some((el) => el.id === this.field.id);
    const canBeContinuous = compiled.isFormulaNumeric || compiled.hasDateFunc || this.isExactDateAndTime;

    this.hasVarTypeMenuOpts =
      this.isValid && !this.isSystem && canBeDiscrete && canBeContinuous && this.allowDiscrete && this.allowContinuous;

    this.tooltipQuickCalcDescription = FieldHelpers.buildAnalyticCalculationDescription(this.field, compiled);

    this.hasSortOpt = this.isValid && this.isDiscrete && !this.isSystem;
    this.hasClearSortOpt = this.encSort != null;

    this.sortDirectionIcon = this.encSort
      ? this.encSort.descending
        ? 'icon-sort-descending'
        : 'icon-sort-ascending'
      : undefined;

    // get encoding info for dual axis
    this.isX2 = this.encoding.x2.some((encField) => encField.id === this.field.id);
    this.isY2 = this.encoding.y2.some((encField) => encField.id === this.field.id);
    this.isX2Relative = this.encoding.x2.some((e: EncDualAxisXField) => e.x === this.field.id);
    this.isY2Relative = this.encoding.y2.some((e: EncDualAxisYField) => e.y === this.field.id);
    this.sibling = this.getContinuousSibling();

    this.isDualAxis = this.isX2 || this.isY2;
    this.isDualAxisRelative = this.isX2Relative || this.isY2Relative;

    this.hasDualAxisOpts = this.isX2 || this.isY2 || (!!this.sibling && !this.isX2Relative && !this.isY2Relative);

    this.canBeBinned =
      (this.chartType === 'vegas' || this.chartType === 'vegasPivotTable') &&
      this.allowBinned &&
      this.isValid &&
      !this.isSystem &&
      compiled.isAttrNumberType &&
      !(compiled.hasDateFunc || this.isExactDateAndTime);
    this.isBinned = compiled.isBinned;
    this.disableBinningForValues = this.encoding.value.some((encField) => encField.id === this.field.id);

    this.caretIcon = !this.isValid ? 'icon-invalid' : this.isDiscrete ? 'icon-discrete' : 'icon-continuous';

    // Keep this simple for now. This is only used for the dual axis styling
    this.isQuickChartType = this.chartType !== 'vegas';

    if (this.encoding.marks.channel !== '*') {
      this.canBeSharedAxis = this.isSharedAxisOptAllowed();
      this.isSharedAxis =
        this.field.groupId !== null && (this.encoding.groups?.some((g) => g.id === this.field.groupId) ?? false);
      this.sharedAxisId = this.field.groupId ?? '';
    } else {
      this.canBeSharedAxis = false;
      this.isSharedAxis = false;
      this.sharedAxisId = '';
    }

    this.isValidField.emit({
      isValid: this.isValid,
      field: this.field,
    });

    this.isVegasPivotTable = this.chartType === 'vegasPivotTable';
    this.isVE = this.chartType === 'vegas';
    this.isMuzeRenderer = ChartFormatHelpers.useMuzeRenderer(this.chartType, this.format);

    if (changes['binSizeToFieldMap'] != null) {
      this.updatePendingBinnedFieldsEncoding();
    }
  }

  showContextMenu($event: MouseEvent) {
    // Launch the context menu after the normal click handling
    setTimeout(() => {
      this.contextMenuService.show.next({
        // Optional - if unspecified, all context menu components will open
        contextMenu: this.menu,
        event: $event,
        item: this.field,
      });
    }, 0);
  }

  validateEncField(field: EncField, view: FlamingoTypes.ModeView, schemaModel: SwitchSchemaModel | undefined) {
    const compiledFormula = CompiledField.fromEncField(view, field);
    if (!compiledFormula.isValid) {
      this.errorMessage = compiledFormula.errors.map((error) => error.message).join('\n');
      return false;
    }
    if (
      compiledFormula.attrName &&
      !EncodingHelpers.validateDuplicateCalcFields(this.model, view.name, compiledFormula.attrName)
    ) {
      return false;
    }
    if (field.frame?.type === ExprFrame.Type.Custom && schemaModel) {
      const result = EncodingHelpers.validateEncodingFieldFrame(field, this.encoding, schemaModel);
      if (!result.valid) {
        this.errorMessage += `${this.errorMessage ? '\n' : ''}${result.error}`;
        return false;
      }
    }

    if (this.encSort && schemaModel) {
      let isValid = true;
      switch (this.encSort.type) {
        case SortType.FIELD:
        case SortType.NESTED: {
          const errors = EncodingHelpers.validateEncSortField(this.encSort, schemaModel);
          isValid = !errors.length;
          if (!this.isValid) {
            this.errorMessage = errors[0];
          }
          break;
        }
      }
      return isValid;
    }

    return true;
  }

  validateConfig(formula: CompiledField) {
    const error = (message: string) => {
      this.errorMessage += `${this.errorMessage ? '\n' : ''}${message}`;
    };

    if (!this.allowScalars) {
      if (formula.isScalar) {
        error('Scalar field is not allowed');
      }
    }

    if (!this.allowAggregates) {
      if (formula.hasAggFunc || formula.isAnalyticCalc) {
        error('Aggregate or Analytic field is not allowed');
      }
    }

    if (!this.allowDiscrete) {
      if (formula.isDiscrete) {
        error('Discrete field is not allowed');
      }
    }

    if (!this.allowContinuous) {
      if (formula.isContinuous) {
        error('Continuous field is not allowed');
      }
    }
  }

  getHasNoAggregateMenuOpt(formula: CompiledField): boolean {
    // We don't allow aggs or scalars, which is what we're toggling between
    if (!this.allowScalars || !this.allowAggregates) {
      return false;
    }

    // Bail on invalid formula
    if (!this.isValid) {
      return false;
    }

    // Bail on expressions we can't wrap|unwrap
    if (formula.isNonScalarCalc || formula.isSystem || formula.attrName == null) {
      return false;
    }

    // Lastly.. no agg menu for dates or
    // fields that reside in values
    return !(
      formula.isAttrDateType ||
      this.encoding.value.some((encField) => {
        return encField.id === this.field.id;
      })
    );
  }

  isSharedAxisOptAllowed() {
    // Measure Names is not supported
    if (this.field.formula === Names.Key) {
      return false;
    }

    // Binned fields are not supported
    if (this.formula?.isBinned) {
      return false;
    }

    // Only rows/columns support shared axis
    const allowedChannels = [...this.encoding.y, ...this.encoding.y2, ...this.encoding.x, ...this.encoding.x2];
    if (!allowedChannels.find((f) => f.id === this.field.id)) {
      return false;
    }
    // Discrete fields are not supported
    if (this.formula != null && this.formula.isDiscrete) {
      return false;
    }
    return true;
  }

  getContinuousSibling() {
    const colIndex = [...(this.encoding?.column ?? []), ...(this.encoding?.x ?? [])].findIndex(
      (encField) => encField.id === this.field.id
    );
    const rowIndex = [...(this.encoding?.row ?? []), ...(this.encoding?.y ?? [])].findIndex(
      (encField) => encField.id === this.field.id
    );
    let siblingField: EncField | undefined;
    if (colIndex > 0) {
      siblingField = [...(this.encoding?.column ?? []), ...(this.encoding?.x ?? [])][colIndex - 1];
    } else if (rowIndex > 0) {
      siblingField = [...(this.encoding?.row ?? []), ...(this.encoding?.y ?? [])][rowIndex - 1];
    }
    if (siblingField?.variable.type === VariableType.CONTINUOUS) {
      const siblingHasDualAxis = [...this.encoding.x2, ...this.encoding.y2].some(
        (e: EncDualAxisYField | EncDualAxisXField) => {
          return (e as EncDualAxisYField).y === siblingField?.id || (e as EncDualAxisXField).x === siblingField?.id;
        }
      );
      return siblingHasDualAxis ? undefined : siblingField;
    } else {
      return;
    }
  }

  dispatchEncodingUpdate(result: EncodingResult, formats?: ChartTypes.FieldFormatMap) {
    this.update.emit({ $result: result, $formats: formats });
  }

  setAggregate(aggregate: ExprCall.Name | null) {
    if (!this.isValid || this.formula == null || this.view == null) {
      return;
    }

    const updatedEncoding = this.updateFieldService.updateAggregate(
      aggregate,
      this.field,
      this.formula,
      this.model,
      this.view,
      this.encoding,
      this.allowContinuous,
      this.allowDiscrete
    );

    if (updatedEncoding) {
      this.dispatchEncodingUpdate(updatedEncoding);
    }
  }

  setVariableType(type: VariableType) {
    if (this.field.id && this.view != null) {
      const updatedEncoding = this.updateFieldService.updateVariableType(type, this.view, this.encoding, this.field);
      this.dispatchEncodingUpdate(updatedEncoding);
    }
  }

  /**
   * Assigns DATEPART type.  Converts variable type if needed.
   * If you change a field from a DateTrunc to a DatePart, we should make the field Discrete.
   * If you change a field from a DatePart to a DateTrunc, we should make the field Continuous.
   * If you change from DatePart to a different DatePart, or a DateTrunc to a different DateTrunc, we don’t change the type.
   * Validate that this field is allowed to be discrete as well
   *
   * @param {DateTime.Unit} part one of DateTime.Unit.YEAR, QTR, MONTH, etc.
   */
  setDatePart(part: DateTime.Unit) {
    if (this.formula != null && this.view != null) {
      const newField = this.formula.withNewFormula(
        FormulaOperations.datePart(this.formula, part),
        !this.isDatePart && this.allowDiscrete ? Attr.Type.Discrete : this.formula.varType
      );
      this.dispatchEncodingUpdate(
        this.updateFieldService.updateFieldEncoding(this.view, this.encoding, this.field, newField.toModifyField())
      );
    }
  }

  /**
   * Assigns DATETRUNC type.  Converts variable type if needed.
   * If you change a field from a DateTrunc to a DatePart, we should make the field Discrete.
   * If you change a field from a DatePart to a DateTrunc, we should make the field Continuous.
   * If you change from DatePart to a different DatePart, or a DateTrunc to a different DateTrunc, we don’t change the type.
   * Validate that this field is allowed to be continuous as well
   *
   * @param {DateTime.Unit} part one of DateTime.Unit.YEAR, QTR, MONTH, etc.
   */
  setDateTrunc(part: DateTime.Unit) {
    if (this.formula != null && this.view != null) {
      const newField = this.formula.withNewFormula(
        FormulaOperations.dateTrunc(this.formula, part),
        !this.isDateTrunc && this.allowContinuous ? Attr.Type.Continuous : this.formula.varType
      );
      this.dispatchEncodingUpdate(
        this.updateFieldService.updateFieldEncoding(this.view, this.encoding, this.field, newField.toModifyField())
      );
    }
  }

  setWeekStart(weekStart: DateTime.WeekStart): void {
    this.setWeekOrYearStart(weekStart, undefined);
  }

  setYearStart(yearStart: DateTime.YearStart): void {
    this.setWeekOrYearStart(this.weekStart, yearStart);
  }

  setWeekOrYearStart(weekStart?: DateTime.WeekStart, yearStart?: DateTime.YearStart): void {
    let newField: CompiledField | undefined;

    if (this.formula?.dateFuncUnit) {
      const dateFunc = this.isDateTrunc ? FormulaOperations.dateTrunc : FormulaOperations.datePart;
      newField = this.formula.withNewFormula(
        dateFunc(this.formula, this.formula.dateFuncUnit, weekStart, yearStart),
        this.formula.varType
      );
    } else if (this.formula?.isAttrDateType) {
      const dateFuncUnit = weekStart ? DateTime.Unit.Week : DateTime.Unit.Year;
      newField = this.formula.withNewFormula(
        FormulaOperations.dateTrunc(this.formula, dateFuncUnit, weekStart, yearStart),
        this.allowContinuous ? Attr.Type.Continuous : Attr.Type.Discrete
      );
    }

    if (newField && this.view != null) {
      this.dispatchEncodingUpdate(
        this.updateFieldService.updateFieldEncoding(this.view, this.encoding, this.field, newField.toModifyField())
      );
    }
  }

  setTrend(trend: ExprForm | null) {
    if (this.formula == null || this.view == null) {
      return;
    }

    const update = this.updateFieldService.updateTrend(trend, this.field, this.formula, this.view, this.encoding);

    if (update) {
      this.dispatchEncodingUpdate(update.encResult, update.fieldFormats);
    }
  }

  setLegendSplit(split: boolean) {
    this.splitLegend.emit(split);
  }

  openTableCalcSettings() {
    if (!this.formula || this.view == null) {
      return;
    }
    this.tableCalcDialogService.open({
      view: this.view,
      encoding: this.encoding,
      field: this.formula,
      onApply: (field: CompiledField) => {
        const update = this.updateFieldService.updateTableCalcSettings(this.field, field, this.view, this.encoding);
        if (update != null) {
          this.dispatchEncodingUpdate(update.encResult, update.fieldFormats);
        }
      },
    });
  }

  doSort() {
    if (this.view != null) {
      this.sortModal.open({
        view: this.view,
        encoding: this.encoding,
        field: this.field,
        fieldFormatMap: this.fieldFormatMap,
        sort: this.encSort,
        vizToken: this.vizToken,
        onApply: (sort: EncSort) => {
          this.dispatchEncodingUpdate(EncodingHelpers.addSort(this.encoding, sort));
        },
      });
    }
  }

  removeField(): void {
    this.remove.emit({ $field: this.field });
  }

  onRemoveSort(): void {
    this.removeSort.emit({ $source: this.formula?.formula });
  }

  onToggleDualAxis(): void {
    if (this.formula != null) {
      const channel = this.encoding.marks.channel === 'y' ? 'row' : 'column';
      this.toggleDualAxis.emit({ $field: this.formula, $sibling: this.sibling, $channel: channel });
    }
  }

  onToggleSharedAxis(): void {
    if (this.isSharedAxis) {
      // Remove from the group
      this.dispatchEncodingUpdate(SharedAxisHelpers.removeFieldFromSharedAxis(this.encoding, this.field));
    } else {
      // Create a new shared axis
      this.dispatchEncodingUpdate(SharedAxisHelpers.createSharedAxisWithEncField(this.encoding, this.field));
    }
  }

  onAddSelectedFieldToFormat(): void {
    this.updateFieldService.selectFieldToFormat(this.field);
  }

  onToggleBin() {
    if (this.isBinned) {
      // Re-apply aggregates if one was applied before binning
      if (this.preBinAggFunc != null) {
        this.setAggregate(this.preBinAggFunc);
      } else {
        this.setScalar(null);
      }
    } else {
      // Reset aggregations if an aggregation was applied.
      switch (this.aggFunc) {
        case ExprCall.Name.Sum.toUpperCase():
          this.preBinAggFunc = ExprCall.Name.Sum;
          break;
        case ExprCall.Name.Count.toUpperCase():
          this.preBinAggFunc = ExprCall.Name.Count;
          break;
        case ExprCall.Name.CountD.toUpperCase():
          this.preBinAggFunc = ExprCall.Name.CountD;
          break;
        case ExprCall.Name.CountDA.toUpperCase():
          this.preBinAggFunc = ExprCall.Name.CountDA;
          break;
        case ExprCall.Name.Avg.toUpperCase():
          this.preBinAggFunc = ExprCall.Name.Avg;
          break;
        case ExprCall.Name.Max.toUpperCase():
          this.preBinAggFunc = ExprCall.Name.Max;
          break;
        case ExprCall.Name.Min.toUpperCase():
          this.preBinAggFunc = ExprCall.Name.Min;
          break;
        default:
          this.preBinAggFunc = null;
          break;
      }

      this.setScalar(ScalarExpr.Bin);
    }
  }

  onClickBinSettingsMenu() {
    if (!this.formula) {
      return;
    }

    this.binningModalService.open({
      targetField: this.formula,
      vizToken: this.vizToken,
      fieldFormatMap: this.fieldFormatMap ?? {},
      onApplyFieldBinSize: (binSize, field) => {
        this.onFieldBinSizeUpdated(this.field, field, binSize);
      },
    });
  }

  setScalar(scalar: ScalarExpr | null) {
    const field = this.field;
    const formula = this.formula;
    let encoding: Encoding = this.encoding;

    if (!this.isValid || !formula || !field?.id || !formula.attrName || !this.view) {
      return;
    }

    if (scalar === null) {
      this.dispatchEncodingUpdate(
        this.updateFieldService.updateFieldEncoding(this.view, encoding, field, {
          id: field.id,
          formula: FormulaOperations.access(formula),
          variable: {
            type: this.updateFieldService.getAttrType(this.model, this.view, formula.attrName),
          },
        })
      );
    } else if (scalar === ScalarExpr.Bin) {
      this.enableBinning(field, formula, this.vizToken);
    }
  }

  enableBinning(field: EncField, formula: CompiledField, token: string) {
    const { attrName } = formula;

    if (!attrName) {
      return;
    }

    this.pendingBinSizeMap.set(attrName, {
      field: field,
      formula: formula,
    });

    if (this.binSizeToFieldMap?.has(attrName)) {
      this.updatePendingBinnedFieldsEncoding();
    } else {
      this.getBinSize.emit({ columnName: attrName, token });
    }
  }

  onFieldBinSizeUpdated(field: EncField, formula: CompiledField, binSize: number) {
    if (!this.view || formula.binSize === binSize) {
      return;
    }
    const view = this.view;

    this.dispatchEncodingUpdate(
      this.updateFieldService.updateFieldEncoding(view, this.encoding, field, {
        id: field.id || '',
        formula: FormulaOperations.bin(formula, binSize),
        variable: field.variable,
      })
    );
  }

  updatePendingBinnedFieldsEncoding() {
    if (!this.view) {
      return;
    }
    const view = this.view;

    for (const columnName of this.pendingBinSizeMap.keys()) {
      const binSize = this.binSizeToFieldMap?.get(columnName)?.binSize;

      if (binSize) {
        const info = this.pendingBinSizeMap.get(columnName);
        if (!info || !info.field || !info.formula) {
          return;
        }
        const source = FormulaOperations.bin(info.formula, binSize);
        let updateEncoding = this.encoding;
        if (SharedAxisHelpers.isSharedAxisMember(this.encoding, info.field.id ?? '')) {
          updateEncoding = SharedAxisHelpers.removeFieldFromSharedAxis(updateEncoding, info.field)?.encoding;
        }

        this.dispatchEncodingUpdate(
          this.updateFieldService.updateFieldEncoding(view, updateEncoding, info.field, {
            id: info.field?.id || '',
            formula: source,
            variable: info.field.variable,
          })
        );

        this.pendingBinSizeMap.delete(columnName);
      }
    }
  }
}
